import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './useAuth';
import type { User } from '@supabase/supabase-js';

interface AdminProfile {
  id: string;
  name: string;
  role: string;
  auth_user_id: string;
}

interface AdminCheckResult {
  isAdmin: boolean;
  checking: boolean;
  adminName: string;
  role: string;
  user: User | null;
  profile: AdminProfile | null;
}

export function useAdminCheck(): AdminCheckResult {
  const { user, initialized } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const [adminName, setAdminName] = useState('');
  const [role, setRole] = useState('');
  const [profile, setProfile] = useState<AdminProfile | null>(null);

  useEffect(() => {
    if (!initialized) return;

    if (!user) {
      setIsAdmin(false);
      setChecking(false);
      setAdminName('');
      setRole('');
      setProfile(null);
      return;
    }

    const checkAdmin = async () => {
      setChecking(true);
      try {
        const { data, error } = await supabase
          .from('admin_profiles')
          .select('id, name, role, auth_user_id')
          .eq('auth_user_id', user.id)
          .maybeSingle();

        if (!error && data) {
          setIsAdmin(true);
          setAdminName(data.name || '');
          setRole(data.role || '');
          setProfile(data);
        } else {
          setIsAdmin(false);
          setAdminName('');
          setRole('');
          setProfile(null);
        }
      } catch {
        setIsAdmin(false);
        setAdminName('');
        setRole('');
        setProfile(null);
      } finally {
        setChecking(false);
      }
    };

    checkAdmin();
  }, [user, initialized]);

  return { isAdmin, checking, adminName, role, user, profile };
}
